import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Stack,
  Alert,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Info as InfoIcon,
  Code as CodeIcon,
  DataObject as ObjectIcon,
  List as ArrayIcon,
  TextFields as StringIcon,
  Functions as NumberIcon,
  ToggleOn as BooleanIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

function StateVariableDialog({
  open, onClose, stateVar, onSave, nodeId, scope,
}) {
  const [currentStateVar, setCurrentStateVar] = React.useState(stateVar || {
    key: '',
    type: 'string',
    defaultValue: '',
    description: '',
    persistent: false,
  });

  const [validationError, setValidationError] = React.useState('');

  React.useEffect(() => {
    if (stateVar) {
      setCurrentStateVar(stateVar);
    } else {
      setCurrentStateVar({
        key: '',
        type: 'string',
        defaultValue: '',
        description: '',
        persistent: false,
      });
    }
    setValidationError('');
  }, [stateVar, open]);

  const validateForm = () => {
    if (!currentStateVar.key.trim()) {
      setValidationError('Variable name is required');
      return false;
    }

    // Check for valid variable name (alphanumeric and underscore only)
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(currentStateVar.key)) {
      setValidationError('Variable name must start with a letter or underscore and contain only letters, numbers, and underscores');
      return false;
    }

    // Validate default value based on type
    if (currentStateVar.defaultValue !== '') {
      try {
        switch (currentStateVar.type) {
          case 'number':
            if (isNaN(Number(currentStateVar.defaultValue))) {
              setValidationError('Default value must be a valid number');
              return false;
            }
            break;
          case 'boolean':
            if (!['true', 'false', '0', '1'].includes(currentStateVar.defaultValue.toLowerCase())) {
              setValidationError('Default value must be true, false, 0, or 1');
              return false;
            }
            break;
          case 'object':
            if (currentStateVar.defaultValue !== '') {
              JSON.parse(currentStateVar.defaultValue);
            }
            break;
          case 'array':
            if (currentStateVar.defaultValue !== '') {
              JSON.parse(currentStateVar.defaultValue);
            }
            break;
        }
      } catch (e) {
        setValidationError(`Invalid ${currentStateVar.type} format for default value`);
        return false;
      }
    }

    setValidationError('');
    return true;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    // Process default value based on type
    let processedDefaultValue = currentStateVar.defaultValue;
    if (currentStateVar.defaultValue !== '') {
      switch (currentStateVar.type) {
        case 'number':
          processedDefaultValue = Number(currentStateVar.defaultValue);
          break;
        case 'boolean':
          processedDefaultValue = ['true', '1'].includes(currentStateVar.defaultValue.toLowerCase());
          break;
        case 'object':
        case 'array':
          try {
            processedDefaultValue = JSON.parse(currentStateVar.defaultValue);
          } catch (e) {
            processedDefaultValue = currentStateVar.defaultValue;
          }
          break;
      }
    }

    // Always use the scope and nodeId from props
    const saveObj = {
      ...currentStateVar,
      defaultValue: processedDefaultValue,
      scope,
      nodeId,
      // Ensure type is properly set
      type: currentStateVar.type || 'string',
    };

    // Debug logging to verify type information
    console.log('💾 Saving state variable:', {
      key: saveObj.key,
      type: saveObj.type,
      scope: saveObj.scope,
      nodeId: saveObj.nodeId,
      defaultValue: saveObj.defaultValue,
    });

    onSave(saveObj);
    onClose();
  };

  const handleChange = (field, value) => {
    setCurrentStateVar((prev) => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (field === 'key' && validationError) {
      setValidationError('');
    }
  };

  const getTypeInfo = (type) => {
    const typeInfo = {
      string: {
        label: 'String',
        icon: <StringIcon />,
        color: '#1976d2',
        description: 'Text data (default)',
        placeholder: 'Enter text value',
        example: '"Hello World"',
      },
      number: {
        label: 'Number',
        icon: <NumberIcon />,
        color: '#4caf50',
        description: 'Numeric values',
        placeholder: 'Enter number',
        example: '42',
      },
      boolean: {
        label: 'Boolean',
        icon: <BooleanIcon />,
        color: '#ff9800',
        description: 'True/false values',
        placeholder: 'true or false',
        example: 'true',
      },
      object: {
        label: 'Object',
        icon: <ObjectIcon />,
        color: '#9c27b0',
        description: 'Complex data structures',
        placeholder: '{"key": "value"}',
        example: '{"name": "John", "age": 30}',
      },
      array: {
        label: 'Array',
        icon: <ArrayIcon />,
        color: '#e91e63',
        description: 'Lists of values',
        placeholder: '["item1", "item2"]',
        example: '["apple", "banana", "orange"]',
      },
    };

    // If a specific type is requested, return that type's info
    if (type) {
      return typeInfo[type] || typeInfo.string;
    }

    // Otherwise return all type info for dropdown
    return typeInfo;
  };

  const getDefaultValueForType = (type) => {
    switch (type) {
      case 'number': return '0';
      case 'boolean': return 'false';
      case 'object': return '{}';
      case 'array': return '[]';
      default: return '';
    }
  };

  const handleTypeChange = (newType) => {
    handleChange('type', newType);
    // Update default value if it's empty or doesn't match the new type
    if (!currentStateVar.defaultValue || currentStateVar.defaultValue === '') {
      handleChange('defaultValue', getDefaultValueForType(newType));
    }
  };

  const currentTypeInfo = getTypeInfo(currentStateVar?.type || 'string');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {stateVar?.key ? 'Edit State Variable' : 'Add State Variable'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {validationError}
            </Alert>
          )}

          <TextField
            label="Variable Name"
            value={currentStateVar?.key || ''}
            onChange={(e) => handleChange('key', e.target.value)}
            placeholder="e.g., userInput, processedData"
            error={!!validationError}
            helperText="Use camelCase naming (e.g., apiResponse, userData)"
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Data Type</InputLabel>
            <Select
              value={currentStateVar?.type || 'string'}
              onChange={(e) => handleTypeChange(e.target.value)}
              label="Data Type"
            >
              <MenuItem value="string">String</MenuItem>
              <MenuItem value="number">Number</MenuItem>
              <MenuItem value="boolean">Boolean</MenuItem>
              <MenuItem value="object">Object</MenuItem>
              <MenuItem value="array">Array</MenuItem>
            </Select>
            <Box sx={{
              mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1,
            }}
            >
              <Typography variant="caption" color="text.secondary">
                <strong>Type:</strong>
                {' '}
                {currentStateVar?.type || 'string'}
                {' '}
                -
                {' '}
                {getTypeInfo(currentStateVar?.type || 'string').description}
              </Typography>
            </Box>
          </FormControl>

          {/* Show scope as disabled field for clarity */}
          <TextField
            label="Scope"
            value={scope === 'node' ? `Node (${nodeId || ''})` : (scope || 'global')}
            disabled
            helperText={`This variable will be available in ${scope} scope`}
            fullWidth
          />

          <TextField
            label="Default Value"
            value={currentStateVar?.defaultValue || ''}
            onChange={(e) => handleChange('defaultValue', e.target.value)}
            placeholder="Enter default value"
            helperText={`Enter a ${currentStateVar?.type || 'string'} value`}
            multiline={currentStateVar?.type === 'object' || currentStateVar?.type === 'array'}
            rows={currentStateVar?.type === 'object' || currentStateVar?.type === 'array' ? 3 : 1}
            fullWidth
          />

          <TextField
            label="Description"
            value={currentStateVar?.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            multiline
            rows={2}
            placeholder="Describe what this variable stores"
            fullWidth
          />

          <FormControlLabel
            control={(
              <Switch
                checked={currentStateVar?.persistent || false}
                onChange={(e) => handleChange('persistent', e.target.checked)}
              />
            )}
            label={(
              <Box>
                <Typography variant="body2">Persistent</Typography>
                <Typography variant="caption" color="text.secondary">
                  Variable survives flow restarts
                </Typography>
              </Box>
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!!validationError}
          startIcon={<CodeIcon />}
        >
          Save Variable
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StateVariableDialog;
